import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appAutoFormatDate]',
  standalone: true,
})
export class AutoFormatDateDirective {
  @Input() dateFormat!: string;

  private previousValue: string = '';
  private previousCaretPosition: number = 0;

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    const input = this.el.nativeElement.value;
    const formattedValue = this.formatDate(input);
    this.el.nativeElement.value = formattedValue;

    const caretPosition = this.getCaretPositionOnInput(this.previousValue, formattedValue, this.previousCaretPosition);
    this.el.nativeElement.setSelectionRange(caretPosition, caretPosition);

    this.previousValue = formattedValue;
    this.previousCaretPosition = caretPosition;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const { key, shiftKey } = event;

    this.previousValue = this.el.nativeElement.value;
    this.previousCaretPosition = this.el.nativeElement.selectionStart || 0;

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      event.preventDefault();
      this.incrementSegment(key === 'ArrowUp' ? 1 : -1);
    } else if (key === 'Tab') {
      event.preventDefault();
      this.moveToNextSegment(shiftKey);
    } else if (key === 'Enter') {
      event.preventDefault();
      this.el.nativeElement.blur();
    }
  }

  private formatDate(input: string): string {
    if (!this.dateFormat) return input;

    const segments = this.dateFormat.match(/[A-Za-z]+/g) || []; // e.g. ['dd', 'MM', 'yyyy']
    const delimiters = this.dateFormat.match(/[^a-zA-Z]+/g)?.slice(0, 2) || []; // e.g. ['/', '/']
    const inputDigits = input.replace(/\D/g, ''); // Remove non-numeric characters

    let inputIndex = 0;

    return segments
      .map((segment, i) => {
        if (inputIndex >= inputDigits.length) return '';
        const segmentValue = inputDigits.slice(inputIndex, inputIndex + segment.length);
        inputIndex += segmentValue.length;
        return segmentValue + (segmentValue.length === segment.length ? delimiters[i] || '' : '');
      })
      .join('');
  }

  private incrementSegment(increment: number): void {
    const inputElement = this.el.nativeElement;
    const inputValue = inputElement.value;
    const caretPosition = inputElement.selectionStart || 0;
    const segments = this.dateFormat.match(/[A-Za-z]+/g) || [];
    const delimiters = this.dateFormat.match(/[^a-zA-Z]+/g) || [];

    const { segmentIndex, segmentStart } = this.findCurrentSegment(caretPosition, segments, delimiters);

    if (segmentIndex !== -1) {
      const segment = segments[segmentIndex];
      const segmentValue = inputValue.slice(segmentStart, segmentStart + segment.length);
      const updatedValue = this.adjustValue(segmentValue, segment, increment);

      const beforeCursor = inputValue.slice(0, segmentStart);
      const afterCursor = inputValue.slice(segmentStart + segment.length);

      inputElement.value = beforeCursor + updatedValue + afterCursor;

      const newCaretStart = segmentStart;
      const newCaretEnd = segmentStart + updatedValue.length;
      requestAnimationFrame(() => {
        inputElement.setSelectionRange(newCaretStart, newCaretEnd);
      });

      inputElement.dispatchEvent(new Event('input'));

      const formControl = inputElement.getAttribute('formControlName');
      if (formControl) {
        const ngControl = inputElement.__ngContext__;
        if (ngControl?.control) {
          ngControl.control.setValue(inputElement.value, { emitEvent: true });
          ngControl.control.markAsDirty();
          ngControl.control.markAsTouched();
        }
      }
    }
  }

  private adjustValue(value: string, segment: string, increment: number): string {
    const numericValue = parseInt(value, 10) || 0;

    const currentYear = new Date().getFullYear();
    const segmentAdjustments = {
      dd: { min: 1, max: 31 },
      MM: { min: 1, max: 12 },
      yyyy: { min: currentYear - 27, max: currentYear },
    };

    const adjustment = segmentAdjustments[segment];

    if (adjustment) {
      const adjusted = Math.min(Math.max(numericValue + increment, adjustment.min), adjustment.max);
      return segment === 'yyyy' ? adjusted.toString() : adjusted.toString().padStart(2, '0');
    }

    return value;
  }

  private getCaretPositionOnInput(previousValue: string, formattedValue: string, previousCaretPosition: number): number {
    if (formattedValue.length < previousValue.length) {
      return Math.max(previousCaretPosition - 1, 0);
    }

    const inputDigits = formattedValue.replace(/\D/g, '');

    let digitCount = 0;

    const lastDigitIndex = [...formattedValue].reduce((acc, char, i) => {
      if (/\d/.test(char)) {
        digitCount++;
        if (digitCount === inputDigits.length) {
          return i;
        }
      }
      return acc;
    }, -1);

    if (lastDigitIndex === -1) {
      return formattedValue.length;
    }

    const nextChar = formattedValue[lastDigitIndex + 1];
    if (nextChar && /\D/.test(nextChar)) {
      return lastDigitIndex + 2;
    }

    return lastDigitIndex + 1;
  }

  private moveToNextSegment(shiftKey: boolean): void {
    const inputElement = this.el.nativeElement;
    const caretPosition = inputElement.selectionStart || 0;
    const segments = this.dateFormat.match(/[A-Za-z]+/g) || [];
    const delimiters = this.dateFormat.match(/[^a-zA-Z]+/g) || [];

    const { segmentIndex } = this.findCurrentSegment(caretPosition, segments, delimiters);

    if (segmentIndex !== -1) {
      const nextSegmentIndex = shiftKey ? (segmentIndex - 1 + segments.length) % segments.length : (segmentIndex + 1) % segments.length;

      const segmentStart = this.getSegmentStart(nextSegmentIndex, segments, delimiters);
      const segmentEnd = segmentStart + segments[nextSegmentIndex].length;

      inputElement.setSelectionRange(segmentStart, segmentEnd);
    }
  }

  private findCurrentSegment(
    caretPosition: number,
    segments: string[],
    delimiters: string[],
  ): { segmentIndex: number; segmentStart: number } {
    let currentIndex = 0;

    const segmentIndex = segments.findIndex((segment, i) => {
      const segmentLength = segment.length;
      const isInSegment = caretPosition >= currentIndex && caretPosition < currentIndex + segmentLength;

      if (!isInSegment) {
        currentIndex += segmentLength + (delimiters[i]?.length || 0);
      }

      return isInSegment;
    });

    return {
      segmentIndex,
      segmentStart: segmentIndex !== -1 ? this.getSegmentStart(segmentIndex, segments, delimiters) : -1,
    };
  }

  private getSegmentStart(segmentIndex: number, segments: string[], delimiters: string[]): number {
    return segments.slice(0, segmentIndex).reduce((acc, segment, i) => acc + segment.length + (delimiters[i]?.length || 0), 0);
  }
}
